import type {CSSProperties} from 'react'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {clsx} from 'clsx'
import {KontentResponsivePicture} from '@/products/flexible-web-toolkit/components/responsive-picture'
import type {Block} from '@/services/kontent-ai/types'
import {spacing} from '@/products/flexible-web-toolkit/styles'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'

export type FloatingComponentContentItem = IContentItem<{
	item: Elements.LinkedItemsElement
	position: Elements.MultipleChoiceElement
	/** @deprecated Use itemBgColorPicker instead */
	itemBgColor: Elements.MultipleChoiceElement
	/** New color picker field - takes priority over itemBgColor */
	itemBgColorPicker: Elements.CustomElement
	image: Elements.AssetsElement
	mobileImage: Elements.AssetsElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
}>

export const FloatingComponentBlock: Block<FloatingComponentContentItem> = ({
	block,
	BlockMapper,
	page,
	...context
}) => {
	const blockItem = block.elements.item[0]
	const itemPosition =
		block.elements.position[0]?.codename === 'right'
			? 'md:flex-row-reverse'
			: null

	// Color priority: itemBgColorPicker (new picker) → itemBgColor (deprecated dropdown) → page config → transparent
	const {bgClass, hexCode} = colorWithFallback({
		blockColor: block.elements.itemBgColorPicker,
		blockColorLegacy: block.elements.itemBgColor,
		pageConfig: page.elements.config[0],
	})
	// If the effective color is transparent (e.g. legacy 'none'), keep the background transparent
	// so the background image shows through. For legacy colors like 'dark'/'light', bgClass may be
	// null, so fall back to using the hexCode via inline styles.
	const isTransparentColor = !hexCode || hexCode === 'transparent'
	const itemBackgroundColorClass =
		bgClass && !isTransparentColor ? bgClass : 'bg-transparent'
	const itemBackgroundColorStyle =
		!bgClass && hexCode && !isTransparentColor
			? {backgroundColor: hexCode}
			: undefined

	const mobileImageEl = block.elements.mobileImage[0] ?? null
	const desktopImage = block.elements.image[0] ?? mobileImageEl

	// Container uses CSS aspect-ratio on desktop (md+) so the background image
	// fills without cropping. On mobile the height stays auto (content-driven).
	const containerStyle = {
		'--img-ar':
			desktopImage?.width && desktopImage.height
				? `${desktopImage.width} / ${desktopImage.height}`
				: undefined,
	} as CSSProperties

	const spacingChoice =
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- not unnecessary as key may evaluate to undefined
		spacing[
			block.elements.snippetSpacingSpacing[0]
				?.codename as keyof typeof spacing
		] || spacing.default

	return (
		<div
			className={clsx(
				`relative flex w-full items-center justify-center p-3 md:justify-start md:p-10`,
				desktopImage?.width &&
					desktopImage.height &&
					'md:[aspect-ratio:var(--img-ar)]',
				itemPosition,
				spacingChoice
			)}
			style={containerStyle}
		>
			{desktopImage ? (
				<KontentResponsivePicture
					className="h-full w-full object-cover"
					desktopAsset={desktopImage}
					fallbackHeight={600}
					fallbackWidth={1920}
					mobileAsset={mobileImageEl}
					pictureClassName="absolute inset-0 h-full w-full"
					quality={100}
					sizes="100vw"
				/>
			) : null}
			{blockItem && desktopImage ? (
				<div
					className={clsx(
						itemBackgroundColorClass,
						`z-10 max-h-[100%] flex-shrink overflow-auto p-8 md:max-w-[70%] md:flex-none lg:max-w-[45%]`
					)}
					style={itemBackgroundColorStyle}
				>
					<BlockMapper
						blocks={block.elements.item}
						page={page}
						{...context}
					/>
				</div>
			) : null}
		</div>
	)
}
